import "@/styles/app.css"
import React from "react"
import { createRoot } from "react-dom/client"
import { RouterProvider } from "@tanstack/react-router"
import { router } from "@/router"
import { QueryProvider } from "@/providers/query"
import { GlobalValidationProvider } from "@/providers/validation"
import { ConfigProvider } from "@/providers/config"
import toast, { Toaster } from "react-hot-toast"
import { AuthProvider } from "@/providers/auth"
import { useVersionCheck } from "@/hooks/use-version-check"
import { VersionNotification } from "@/components/version-notification"

// Make React and ReactDOM available globally for UMD builds
declare global {
  interface Window {
    React: typeof React
    ReactDOM: typeof import("react-dom")
  }
}

// Inner component that uses hooks requiring providers
function AppContent() {
  const { showNotification, handleDismiss, handleRefresh } = useVersionCheck()

  return (
    <>
      <RouterProvider router={router} />
      {showNotification && (
        <VersionNotification
          onRefresh={handleRefresh}
          onDismiss={handleDismiss}
        />
      )}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            padding: "0",
            background: "transparent",
            boxShadow: "none",
          },
          success: {
            style: {
              padding: "0",
              background: "transparent",
              boxShadow: "none",
            },
            iconTheme: {
              primary: "hsl(var(--su))",
              secondary: "hsl(var(--suc))",
            },
          },
          error: {
            style: {
              padding: "0",
              background: "transparent",
              boxShadow: "none",
            },
            iconTheme: {
              primary: "hsl(var(--er))",
              secondary: "hsl(var(--erc))",
            },
          },
          loading: {
            iconTheme: {
              primary: "hsl(var(--in))",
              secondary: "hsl(var(--inc))",
            },
          },
          custom: {
            style: {
              padding: "0",
              background: "transparent",
              boxShadow: "none",
            },
          },
        }}
        containerStyle={{
          top: 20,
        }}
      >
        {(t) => (
          <div
            className={`alert ${
              t.type === "success"
                ? "alert-success"
                : t.type === "error"
                  ? "alert-error"
                  : t.type === "loading"
                    ? "alert-info"
                    : "alert-info"
            } flex max-w-md min-w-[300px] items-center justify-between shadow-lg ${
              t.visible ? "animate-enter" : "animate-leave"
            }`}
          >
            <div className="flex items-center gap-3">
              {t.type === "success" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
              {t.type === "error" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
              {t.type === "loading" && (
                <span className="loading loading-spinner"></span>
              )}
              <div className="text-sm font-medium">
                {typeof t.message === "function" ? t.message(t) : t.message}
              </div>
            </div>
            {t.type !== "loading" && (
              <button
                onClick={() => toast.dismiss(t.id)}
                className="btn btn-sm btn-circle btn-ghost"
                aria-label="Close"
              >
                ✕
              </button>
            )}
          </div>
        )}
      </Toaster>
    </>
  )
}

// Router provider component with all providers
function App() {
  return (
    <ConfigProvider>
      <GlobalValidationProvider>
        <QueryProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </QueryProvider>
      </GlobalValidationProvider>
    </ConfigProvider>
  )
}

// Function to mount a React app to a specific DOM element
// Error Boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary caught error:", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center p-8">
          <div className="card bg-error text-error-content">
            <div className="card-body">
              <h2 className="card-title">Something went wrong</h2>
              <p>{this.state.error?.message}</p>
              <div className="card-actions justify-end">
                <button
                  className="btn btn-ghost"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export function mountReactApp(elementId: string) {
  const element = document.getElementById(elementId)
  if (!element) {
    console.error(`Element with id '${elementId}' not found`)
    return
  }

  // Check if React is already mounted to this element
  if (element.hasAttribute("data-react-mounted")) {
    console.warn(`React app already mounted to element '${elementId}'`)
    return
  }

  const root = createRoot(element)
  root.render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>,
  )

  // Mark element as having React mounted
  element.setAttribute("data-react-mounted", "true")
}

// Main initialization function
export function initReactApp() {
  // Check if already initialized
  if (
    window.__reactAppInitialized &&
    document.querySelector("#react-app [data-react-root]")
  ) {
    console.log("React app already initialized, skipping...")
    return
  }

  const reactAppElement = document.getElementById("react-app")
  if (!reactAppElement) {
    return
  }

  // Add error boundary and debugging
  try {
    mountReactApp("react-app")
  } catch (error) {
    console.error("Error mounting React app:", error)
    console.error(
      "This often happens when useContext hooks are called outside their Provider",
    )

    // Display error in the DOM
    const element = document.getElementById("react-app")
    if (element) {
      element.innerHTML = `
        <div class="flex h-full w-full items-center justify-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-error mb-4">Error Loading Page</h1>
            <p class="text-base-content">Failed to initialize React application</p>
            <p class="text-sm text-base-content-secondary mt-2">${error}</p>
          </div>
        </div>
      `
    }
  }
}

// Export router for use in other parts of the app
export { router }

// Auto-initialize the React app when the script loads
// Add a flag to prevent double initialization
declare global {
  interface Window {
    __reactAppInitialized?: boolean
  }
}

if (typeof window !== "undefined" && !window.__reactAppInitialized) {
  window.__reactAppInitialized = true

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initReactApp)
  } else {
    initReactApp()
  }
}
